<template>
  <div class="w-full flex flex-col gap-1 p-1 border border-border-normal" style="border-radius: 20px;">
    <!-- Header -->
    <div class="w-full flex items-center px-3 py-2">
      <CreatorPartnershipIcon class="text-icon-normal" />
      <BaseText type="label" size="sm" class="text-text-muted px-2">
        Creator Partnerships
      </BaseText>
      <BaseText v-if="partnerSummaryData" type="body" size="sm" class="text-text-normal px-2 fade-in">
        {{ subtitleText }}
      </BaseText>
      <button class="ml-auto flex items-center pl-2 pr-0.5 py-1 rounded-md transition-colors duration-100 hover:bg-neutral-25"
      @click="$emit('viewPartnerAds')">
        <BaseText type="label" size="sm" class="text-text-muted">
          View Partner Ads
        </BaseText>
        <ChevronIcon class="transform -rotate-90" />
      </button>
    </div>
    <!-- Content -->
    <div class="w-full grid gap-x-1 p-1 bg-neutral-25" style="grid-template-columns: 224px 1fr; border-radius: 14px; height: 244px;">
      <!-- Summary Stats/Graph -->
      <div  class="w-full h-full flex flex-col gap-2 p-2 bg-white card-shadow rounded-xl">
        <!-- Loading State -->
        <div v-if="loading" class="w-full h-full flex items-center justify-center">
          <div class="flex flex-col items-center gap-2">
            <ScrapingLoadingIcon :width="80" :height="80" class="opacity-60 flex-shrink-0" />
          </div>
        </div>
        <!-- Graph Area -->
        <div v-if="!loading && partnerSummaryData" class="relative w-full fade-in" style="height: 148px;">
          <div ref="graphArea" class="absolute -top-2.5 left-0 bottom-0 right-0" />
          <BaseText type="label" size="md" class="text-text-muted absolute left-1/2 transform -translate-x-1/2" style="top: 64px">
            {{ partnerSummaryData.partnership.percentage }}%
          </BaseText>
        </div>
        <!-- Summary Stats -->
        <div v-if="!loading && partnerSummaryData" class="w-full flex flex-col gap-2 fade-in">
          <div class="w-full flex items-center gap-2 p-1">
            <div class="w-2.5 h-2.5 rounded-sm" style="background-color: #0097FF;" />
            <BaseText type="body" size="sm" class="text-text-muted">
              Partnership
            </BaseText>
            <BaseText type="body" size="sm" class="ml-auto text-text-muted">
              {{ partnerSummaryData.partnership.count.toLocaleString() }}
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal" style="width: 30px; text-align: right;">
              {{ partnerSummaryData.partnership.percentage }}%
            </BaseText>
          </div>
          <div class="w-full flex items-center gap-2 p-1">
            <div class="w-2.5 h-2.5 rounded-sm" style="background-color: #7D61D8;" />
            <BaseText type="body" size="sm" class="text-text-muted">
              Branded
            </BaseText>
            <BaseText type="body" size="sm" class="ml-auto text-text-muted">
              {{ partnerSummaryData.branded.count.toLocaleString() }}
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal" style="width: 30px; text-align: right;">
              {{ partnerSummaryData.branded.percentage }}%
            </BaseText>
          </div>
        </div>
      </div>
      <!-- CREATORS -->
      <div class="relative w-full h-full bg-white p-2 card-shadow rounded-xl min-h-0 scroll-container">
        <div class="w-full flex flex-col gap-1 min-h-0">
          <!-- Loading Skeletons -->
          <template v-if="loading">
            <div v-for="i in 4" :key="`creator-preview-skeleton-${i}`" class="creator-row whitespace-nowrap">
              <div class="w-11 h-11 rounded-full skeleton" />
              <div class="w-full flex flex-col gap-0.5 min-w-0">
                <div class="w-28 h-5 rounded-md skeleton" />
                <div class="w-16 h-5 rounded-md skeleton" />
              </div>
              <div class="w-24 h-5 rounded-md skeleton" />
              <div class="w-full h-7 rounded-md skeleton" />
            </div>
          </template>
          <!-- Empty State -->
          <div v-if="!loading && !formattedPartners?.length" class="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col gap-2 items-center">
            <TeamSettingsIcon class="text-icon-normal" />
            <BaseText type="label" size="sm" class="text-text-muted">
              No creators found
            </BaseText>
            <BaseText type="body" size="sm" class="text-text-normal text-center" style="max-width: 240px;">
              This brand has not ran any partnership ads with creators yet.
            </BaseText>
          </div>
          <!-- CREATORS LIST -->
          <template v-if="!loading">
            <div v-for="(partner, index) in formattedPartners" :key="`creator-preview-${index}`" class="creator-row fade-in">
              <!-- Profile Picture -->
              <img
                v-if="partner.profilePicUrl && !failedImages[index]"
                :src="partner.profilePicUrl"
                class="w-11 h-11 rounded-full"
                @error="handleImageError(index)"
              >
              <DefaultAvatarIcon v-else :width="44" :height="44" />
              <!-- Name and Handle -->
              <div class="w-full flex flex-col gap-0.5 min-w-0">
                <BaseText type="label" size="sm" class="text-text-muted truncate">
                  {{ partner.currentPageName ?? partner.pageName ?? 'Name Unavailable' }}
                </BaseText>
                <BaseText v-if="partner.handle" type="body" size="sm" class="text-text-normal truncate">
                  @{{ partner.handle }}
                </BaseText>
              </div>
              <!-- Ads Status -->
              <div class="w-full">
                <BaseTooltipV2 class="w-max" :offset="6" position="left" 
                :text="partner.adsTooltip">
                  <div class="flex items-center w-max cursor-default">
                    <svg xmlns="http://www.w3.org/2000/svg" width="4" height="4" viewBox="0 0 4 4" fill="none">
                      <circle cx="2" cy="2" r="2" fill="#40C4AA"/>
                    </svg>
                    <BaseText type="label" size="sm" class="text-text-muted ml-2">
                      {{ partner.liveAdsCount?.toLocaleString() ?? 0 }}
                    </BaseText>
                    <BaseText type="body" size="sm" class="text-text-subdued">
                      /{{ partner.adsCount?.toLocaleString() ?? 0 }}
                    </BaseText>
                    <BaseText type="body" size="sm" class="text-text-normal ml-1">
                      ads
                    </BaseText>
                  </div>
                </BaseTooltipV2>
              </div>
              <!-- Link -->
              <a :href="partner.profileUri" target="_blank" class="flex items-center gap-0.5 py-1 pr-1 pl-1.5 
              rounded-lg border border-border-normal transition-colors duration-100 hover:bg-neutral-50">
                <component :is="partner.linkIcon" class="text-text-subdued" />
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M8.20005 6.3999H13.6V11.7999M13.6 6.3999L6.55005 13.4499" stroke="#808899" 
                  stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </a>
            </div>
          </template>
          <infinite-loading
            v-if="!loading"
            :identifier="infiniteId"
            force-use-infinite-wrapper=".scroll-container"
            @infinite="fetchMorePartners"
          >
            <div slot="spinner">
              <BaseLoadingLogo :margin="2" />
            </div>
            <div slot="no-more" />
            <div slot="no-results" />
          </infinite-loading>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ForeplayAPI from '@/api/foreplayServer'
import ApexCharts from 'apexcharts'
import InfiniteLoading from 'vue-infinite-loading'
import { extractSocialHandle, extractSocialDomain } from '@/utils/spyder/brandSocialsUtils'

import CreatorPartnershipIcon from '@/components/globals/Icons/SpyderV2Icons/CreatorPartnershipIcon.vue'
import DefaultAvatarIcon from '@/components/globals/Icons/DefaultAvatarIcon.vue'
import ScrapingLoadingIcon from '@/components/globals/Icons/SpyderV2Icons/ScrapingLoadingIcon.vue'
import TeamSettingsIcon from '@/components/globals/Icons/SettingsIcons/TeamSettingsIcon.vue'
import ChevronIcon from '@/components/globals/Icons/ChevronIcon.vue'

import MonoMetaIcon from '@/components/globals/Icons/socials/mono/MonoMetaIcon.vue'
import MonoInstagramIcon from '@/components/globals/Icons/socials/mono/MonoIGStrokeIcon.vue'
import OpenExternalIcon from '@/components/globals/Icons/OpenExternalIcon.vue'

const PARTNERS_PER_PAGE = 20

export default {
  name: 'CreatorPartnershipsPreview',
  components: {
    CreatorPartnershipIcon,
    DefaultAvatarIcon,
    ScrapingLoadingIcon,
    TeamSettingsIcon,
    ChevronIcon,
    InfiniteLoading
  },
  props: {
    pageId: {
      required: true
    }
  },
  data() {
    return {
      partners: [],
      stats: null,
      totalCreators: 0,
      loading: true,
      chart: null,
      failedImages: {},
      // Row offset for the next page fetched by vue-infinite-loading.
      offset: 0,
      // Bumped on each fresh fetch to reset vue-infinite-loading's internal state.
      infiniteId: +new Date()
    }
  },
  watch: {
    pageId: {
      handler() {
        this.fetchPartners()
      },
      immediate: true
    }
  },
  computed: {
    formattedPartners () {
      return this.partners.map(partner => {
        const liveAdsCount = partner.liveAdsCount ?? 0
        const adsCount = partner.adsCount ?? 0
        const inactiveAdsCount = adsCount - liveAdsCount
        const domain = extractSocialDomain(partner.profileUri)

        let adsTooltip = `${liveAdsCount.toLocaleString()} active ad${liveAdsCount === 1 ? '' : 's'}`
        if (inactiveAdsCount > 0) {
          adsTooltip += `, ${inactiveAdsCount.toLocaleString()} retired ad${inactiveAdsCount === 1 ? '' : 's'}`
        }

        return {
          ...partner,
          domain,
          handle: extractSocialHandle(partner.profileUri),
          adsTooltip,
          linkIcon: domain === 'facebook' ? MonoMetaIcon : domain === 'instagram' ? MonoInstagramIcon : OpenExternalIcon,
        }
      })
    },
    partnerSummaryData () {
      if (!this.stats) return null
      const { totalLiveAds, partnerLiveAds, nonPartnerLiveAds } = this.stats
      return {
        partnership: { count: partnerLiveAds, percentage: totalLiveAds > 0 ? Math.ceil((partnerLiveAds / totalLiveAds) * 100) : 0 },
        branded: { count: nonPartnerLiveAds, percentage: totalLiveAds > 0 ? Math.floor((nonPartnerLiveAds / totalLiveAds) * 100) : 0 }
      }
    },
    subtitleText () {
      if (!this.partnerSummaryData) return ''
      const { partnerLiveAds } = this.stats
      return `${partnerLiveAds.toLocaleString()} active partner ad${partnerLiveAds === 1 ? '' : 's'} across ${this.totalCreators.toLocaleString()} creator${this.totalCreators === 1 ? '' : 's'}`
    }
  },
  beforeDestroy () {
    if (this.chart) {
      this.chart.destroy()
      this.chart = null
    }
  },
  methods: {
    handleImageError (index) {
      this.$set(this.failedImages, index, true)
    },
    async fetchPartners() {
      if (!this.pageId) return
      this.loading = true
      this.failedImages = {}
      this.partners = []
      this.offset = 0
      try {
        const partnersResponse = await ForeplayAPI.Brands.getBrandPartners(
          this.pageId,
          { limit: PARTNERS_PER_PAGE, offset: 0 }
        )
        this.partners = partnersResponse.results
        this.stats = partnersResponse.liveStats
        this.totalCreators = partnersResponse.total
        this.offset = this.partners.length
        // Restart the infinite loader against the fresh result set.
        this.infiniteId += 1
        this.$nextTick(this.renderChart)
      } catch (error) {
        console.error(error)
      } finally {
        this.loading = false
      }
    },
    // vue-infinite-loading handler: fetch the next page and append.
    async fetchMorePartners ($state) {
      if (!this.pageId || this.partners.length >= this.totalCreators) {
        $state.complete()
        return
      }
      try {
        const partnersResponse = await ForeplayAPI.Brands.getBrandPartners(
          this.pageId,
          { limit: PARTNERS_PER_PAGE, offset: this.offset }
        )
        const more = partnersResponse.results ?? []
        if (more.length) this.partners = this.partners.concat(more)
        this.offset = this.partners.length

        if (more.length < PARTNERS_PER_PAGE || this.partners.length >= this.totalCreators) {
          $state.complete()
        } else {
          $state.loaded()
        }
      } catch (error) {
        console.error(error)
        $state.complete()
      }
    },
    renderChart () {
      if (!this.partnerSummaryData || !this.$refs.graphArea) return

      const partnershipCount = this.partnerSummaryData.partnership.count
      const brandedCount = this.partnerSummaryData.branded.count
      const hasData = (partnershipCount + brandedCount) > 0

      // When there are no ads, render a single gray ring instead of an empty donut.
      const series = hasData ? [partnershipCount, brandedCount] : [1]
      const labels = hasData ? ['Partnership', 'Branded'] : ['No Ads']
      const colors = hasData ? ['#0097FF', '#7D61D8'] : ['#DFE1E8']

      const options = {
        chart: {
          type: 'donut',
          sparkline: { enabled: true },
          animations: { enabled: false }
        },
        series,
        labels,
        colors,
        stroke: {
          width: 0
        },
        plotOptions: {
          pie: {
            expandOnClick: false,
            customScale: 0.73,
            donut: {
              size: '93%',
              labels: { show: false }
            }
          }
        },
        dataLabels: { enabled: false },
        legend: { show: false },
        tooltip: { enabled: false },
        states: {
          hover: { filter: { type: 'none' } },
          active: { filter: { type: 'none' } }
        }
      }

      if (this.chart) {
        this.chart.updateOptions(options)
        return
      }

      this.chart = new ApexCharts(this.$refs.graphArea, options)
      this.chart.render()
    }
  }
}
</script>

<style scoped>
.creator-row {
  display: grid;
  grid-template-columns: 44px 1fr 140px 52px;
  column-gap: 16px;
  align-items: center;
  padding: 4px;
}
.card-shadow {
  box-shadow: 0 6px 12px -3px rgba(24, 35, 68, 0.04), 0 2px 4px -1.5px rgba(24, 35, 68, 0.04), 0 1px 2px -0.75px rgba(24, 35, 68, 0.04);
}

.scroll-container {
  overflow-y: auto;
}
.scroll-container::-webkit-scrollbar {
  height: 12px;
  width: 12px;
}
.scroll-container::-webkit-scrollbar-thumb {
  background-color: #DFE1E6;
  border-radius: 10px;
  border: 3px solid #ffffff;
  cursor: pointer;
}
.scroll-container.loading::-webkit-scrollbar-thumb {
  background-color: #ffffff;
}
.scroll-container::-webkit-scrollbar-thumb:hover {
  background-color: #C1C7D0;
}

.fade-in {
  animation: fadeIn 150ms ease-in-out;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
</style>